/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  CircularProgress,
  Typography,
  Box,
  ListItem,
  ListItemText,
  List,
} from "@material-ui/core";
import { useTranslate, useLocale } from "react-admin";
import { GblContext } from "providers/formatter";
import {
  getApVendorList,
  getTaxProfile,
  createInvoiceDetail,
} from "services/accountPayable";
import { getDepartmentList, getAccountCodeList } from "services/setting";
import DatePickerFormat from "components/DatePickerFormat";
import SnackbarUtils from "utils/SnackbarUtils";
import { addDays, format } from "date-fns";

const DialogImport = (props) => {
  const { open, onClose, jsonData, onSuccess } = props;
  const translate = useTranslate();
  const locale = useLocale();
  const { settingAll, NumberFormat } = useContext(GblContext);
  const { SettingSystem, SettingAp, SettingClosePeriod } = settingAll;

  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [taxProfiles, setTaxProfiles] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [importing, setImporting] = useState(false);
  const [inputDate, setInputDate] = useState(new Date());

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        setLoadingLookups(true);
        const [vendorRes, deptRes, accRes, taxRes] = await Promise.all([
          getApVendorList(),
          getDepartmentList(),
          getAccountCodeList("Ap"),
          getTaxProfile(),
        ]);
        setVendors(vendorRes?.Data || []);
        setDepartments(deptRes?.Data || []);
        setAccounts(accRes?.Data || []);
        setTaxProfiles(taxRes?.Data || []);
      } catch (err) {
        console.error("Error loading lookups:", err);
        SnackbarUtils.error("Failed to load lookup data");
      } finally {
        setLoadingLookups(false);
      }
    };

    if (open) {
      fetchLookups();
    }
  }, [open]);

  // Robust Excel row mapper
  const mapExcelRow = (row, rowIndex) => {
    const getVal = (patterns) => {
      const key = Object.keys(row).find((k) =>
        patterns.some((p) =>
          k
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(p.toLowerCase().replace(/\s+/g, "")),
        ),
      );
      return key ? row[key] : undefined;
    };

    return {
      rowIndex: rowIndex + 2, // 1-based index + header row
      vendorNo: getVal(["VendorNo", "Vendor No"]),
      invNo: getVal(["InvNo", "Inv.No", "Inv. No"]),
      invDate: getVal(["InvDate", "Inv.Date", "Inv. Date"]),
      taxInvNo: getVal(["TaxInvNo", "Tax Inv No", "Tax Inv No."]),
      taxInvDate: getVal(["TaxInvDate", "Tax Inv Date"]),
      itemDescription: getVal([
        "ItemDescription",
        "Item Description",
        "Description",
      ]),
      deptCode: getVal([
        "Dr.DeptCode",
        "DrDeptCode",
        "DeptCode",
        "Dr. Dept Code",
      ]),
      accCode: getVal(["Dr.Acc.Code", "DrAccCode", "AccCode", "Dr. Acc. Code"]),
      netAmt: getVal(["NetAmt", "NetAmount", "Net Amount"]),
      taxProfile: getVal(["TaxProfile", "Tax Profile"]),
      taxAmt: getVal(["TaxAmount", "TaxAmt", "Tax Amount"]),
      total: getVal(["Total"]),
    };
  };

  const parseDDMMYY = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    if (typeof val === "string") {
      const trimmed = val.trim();
      const parts = trimmed.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        let year = parseInt(parts[2], 10);
        if (year < 100) {
          year += 2000;
        }
        const d = new Date(year, month, day);
        if (!isNaN(d.getTime())) {
          return d;
        }
      }
    }
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    return null;
  };

  useEffect(() => {
    if (loadingLookups || !jsonData || jsonData.length === 0) {
      setInvoices([]);
      return;
    }

    const { ClosePeriodGl, ClosePeriodAp } = SettingClosePeriod || {};
    const mapped = jsonData.map((row, idx) => mapExcelRow(row, idx));

    // Group by vendorNo + invNo, but handle missing values gracefully
    const groups = {};
    mapped.forEach((item, idx) => {
      const vNo = (item.vendorNo || "").toString().trim();
      const iNo = (item.invNo || "").toString().trim();

      // If either required grouping key is missing, group under a dummy unique key so we can validate it
      if (!vNo || !iNo) {
        const dummyKey = `__invalid_row_${idx}`;
        groups[dummyKey] = {
          vendorNo: vNo,
          invNo: iNo,
          invDate: item.invDate,
          taxInvNo: item.taxInvNo,
          taxInvDate: item.taxInvDate,
          details: [item],
          isDummy: true,
        };
      } else {
        const key = `${vNo}_${iNo}`;
        if (!groups[key]) {
          groups[key] = {
            vendorNo: vNo,
            invNo: iNo,
            invDate: item.invDate,
            taxInvNo: item.taxInvNo,
            taxInvDate: item.taxInvDate,
            details: [],
          };
        }
        groups[key].details.push(item);
      }
    });

    const list = Object.keys(groups).map((key) => {
      const group = groups[key];
      const rowErrors = [];
      const firstRowIdx = group.details[0]?.rowIndex || "?";

      // 1. Group/Header-level Validation
      if (!group.vendorNo) {
        rowErrors.push(`Row ${firstRowIdx}: Vendor Code is required`);
      }

      const vendor = group.vendorNo
        ? vendors.find((v) => v.VnCode === group.vendorNo)
        : null;
      if (group.vendorNo && !vendor) {
        rowErrors.push(
          `Row ${firstRowIdx}: Vendor "${group.vendorNo}" not found`,
        );
      }

      if (!group.invNo) {
        rowErrors.push(`Row ${firstRowIdx}: Invoice No. is required`);
      } else if (group.invNo.toString().length > 30) {
        rowErrors.push(
          `Row ${firstRowIdx}: Invoice No. cannot exceed 30 characters`,
        );
      }

      let invoiceDate = null;
      if (!group.invDate) {
        rowErrors.push(`Row ${firstRowIdx}: Invoice Date is required`);
      } else {
        invoiceDate = parseDDMMYY(group.invDate);
        if (!invoiceDate) {
          rowErrors.push(
            `Row ${firstRowIdx}: Invalid Invoice Date "${group.invDate}"`,
          );
        } else if (ClosePeriodAp) {
          const closeApDate = new Date(ClosePeriodAp);
          if (invoiceDate <= closeApDate) {
            rowErrors.push(
              `Row ${firstRowIdx}: Invoice Date must be more than close period (ClosePeriodAp: ${format(closeApDate, "dd/MM/yyyy")})`,
            );
          }
        }
      }

      const taxInvDate = group.taxInvDate
        ? parseDDMMYY(group.taxInvDate)
        : invoiceDate;
      if (group.taxInvDate && !parseDDMMYY(group.taxInvDate)) {
        rowErrors.push(
          `Row ${firstRowIdx}: Invalid Tax Invoice Date "${group.taxInvDate}"`,
        );
      }

      // 2. Vendor Credit configurations validation
      let vnCrDeptCode = "";
      let vnCrDeptDesc = "";
      let vnVatCrAccCode = "";
      let vnVatCrAccDesc = "";

      if (vendor) {
        // Cr Dept validation
        if (!vendor.VnCrDeptCode) {
          rowErrors.push(
            `Row ${firstRowIdx}: Credit Department Code is not configured for Vendor "${group.vendorNo}"`,
          );
        } else {
          const crDept = departments.find(
            (d) => d.DeptCode === vendor.VnCrDeptCode,
          );
          if (!crDept) {
            rowErrors.push(
              `Row ${firstRowIdx}: Credit Department "${vendor.VnCrDeptCode}" for Vendor "${group.vendorNo}" not found in departments list`,
            );
          } else {
            vnCrDeptCode = vendor.VnCrDeptCode;
            vnCrDeptDesc = vendor.VnCrDeptDesc || crDept.Description || "";
          }
        }

        // Cr Acc validation
        if (!vendor.VnVatCrAccCode) {
          rowErrors.push(
            `Row ${firstRowIdx}: Credit Account Code is not configured for Vendor "${group.vendorNo}"`,
          );
        } else {
          const crAcc = accounts.find(
            (a) => a.AccCode === vendor.VnVatCrAccCode,
          );
          if (!crAcc) {
            rowErrors.push(
              `Row ${firstRowIdx}: Credit Account "${vendor.VnVatCrAccCode}" for Vendor "${group.vendorNo}" not found in accounts list`,
            );
          } else {
            vnVatCrAccCode = vendor.VnVatCrAccCode;
            vnVatCrAccDesc =
              vendor.VnVatCrAccDesc ||
              (locale === "en-US" ? crAcc.Description : crAcc.Description2) ||
              "";
          }
        }
      }

      // 3. Detail-level Validation
      let sumNet = 0;
      let sumTax = 0;
      let sumTotal = 0;
      const detailLines = group.details.map((detail, dIdx) => {
        const netAmtVal = parseFloat(detail.netAmt);
        const taxAmtVal = parseFloat(detail.taxAmt) || 0;
        const totalVal =
          parseFloat(detail.total) ||
          (isNaN(netAmtVal) ? 0 : netAmtVal) + taxAmtVal;

        sumNet += isNaN(netAmtVal) ? 0 : netAmtVal;
        sumTax += taxAmtVal;
        sumTotal += totalVal;

        if (detail.netAmt === "" || detail.netAmt === undefined) {
          rowErrors.push(`Row ${detail.rowIndex}: Net Amount is required`);
        } else if (isNaN(netAmtVal)) {
          rowErrors.push(
            `Row ${detail.rowIndex}: Net Amount must be a valid number`,
          );
        }

        if (
          detail.taxAmt !== "" &&
          detail.taxAmt !== undefined &&
          isNaN(parseFloat(detail.taxAmt))
        ) {
          rowErrors.push(
            `Row ${detail.rowIndex}: Tax Amount must be a valid number`,
          );
        }

        if (
          detail.total !== "" &&
          detail.total !== undefined &&
          isNaN(parseFloat(detail.total))
        ) {
          rowErrors.push(
            `Row ${detail.rowIndex}: Total must be a valid number`,
          );
        }

        let deptDesc = "";
        if (!detail.deptCode) {
          rowErrors.push(
            `Row ${detail.rowIndex}: Dr Department Code is required`,
          );
        } else {
          const dept = departments.find((d) => d.DeptCode === detail.deptCode);
          if (!dept) {
            rowErrors.push(
              `Row ${detail.rowIndex}: Dr Department "${detail.deptCode}" not found`,
            );
          } else {
            deptDesc = dept.Description || "";
          }
        }

        let accDesc = "";
        if (!detail.accCode) {
          rowErrors.push(`Row ${detail.rowIndex}: Dr Account Code is required`);
        } else {
          const acc = accounts.find((a) => a.AccCode === detail.accCode);
          if (!acc) {
            rowErrors.push(
              `Row ${detail.rowIndex}: Dr Account "${detail.accCode}" not found`,
            );
          } else {
            accDesc =
              (locale === "en-US" ? acc.Description : acc.Description2) || "";
          }
        }

        let taxRate = 0;
        let taxType = "None";
        const taxProfileStr = (detail.taxProfile || "")
          .toString()
          .trim()
          .toUpperCase();
        if (!taxProfileStr && taxAmtVal > 0) {
          rowErrors.push(`Row ${detail.rowIndex}: Tax Profile is required`);
        } else if (taxProfileStr) {
          const taxProfile = taxProfiles.find((t) => t.Code === taxProfileStr);
          if (!taxProfile) {
            rowErrors.push(
              `Row ${detail.rowIndex}: Tax Profile "${detail.taxProfile}" not found`,
            );
          } else {
            taxRate = taxProfile.TaxRate || 0;
            taxType = taxRate > 0 ? "Add" : "None";
          }
        } else {
          const defaultTaxProfile = taxProfiles.find((t) => t.Code === "NONE");
          taxRate = defaultTaxProfile ? defaultTaxProfile.TaxRate : 0;
          taxType = taxRate > 0 ? "Add" : "None";
        }

        const rawItemDesc = detail.itemDescription || "Imported item";
        const invdDesc =
          rawItemDesc.length > 255
            ? rawItemDesc.substring(0, 252) + "..."
            : rawItemDesc;

        return {
          index: dIdx,
          InvhSeq: -1,
          InvdSeq: -1,
          InvdDesc: invdDesc,
          InvdQty: 1,
          UnitCode: SettingAp?.DefaultUnit || "UNT",
          InvdPrice: NumberFormat(netAmtVal || 0),
          InvdTaxA1: NumberFormat(
            taxAmtVal * (SettingSystem?.DefaultCurrencyRate || 1),
          ),
          InvdTaxC1: NumberFormat(taxAmtVal),
          InvdTaxA2: NumberFormat(0),
          InvdTaxC2: NumberFormat(0),
          UnPaid: NumberFormat(totalVal),
          NetAmt: NumberFormat(netAmtVal || 0),
          TotalAmt: NumberFormat(totalVal),
          TotalPrice: NumberFormat(totalVal),
          SumPaid: 0,
          DeptCode: detail.deptCode || "",
          DeptDesc: deptDesc,
          InvdBTaxCr1: vnVatCrAccCode,
          InvdBTaxCr1Desc: vnVatCrAccDesc,
          InvdBTaxCr1DeptCode: vnCrDeptCode,
          InvdBTaxCr1DeptDesc: vnCrDeptDesc,
          InvdBTaxDr: detail.accCode || "",
          InvdBTaxDrDesc: accDesc,
          Tax1Overwrite: true,
          InvdT1Dr: vendor ? vendor.VnVat1DrAccCode : "",
          InvdT1DrDesc: vendor ? vendor.VnVat1DrAccDesc : "",
          InvdT1DrDeptCode: vendor ? vendor.VnVat1DrDeptCode : "",
          InvdT1DrDeptDesc: vendor ? vendor.VnVat1DrDeptDesc : "",
          InvdTaxT1: taxType,
          InvdTaxR1: NumberFormat(taxRate),
          InvdTaxT2: "None",
          InvdTaxR2: NumberFormat(0),
          PfmSeq: null,
          WhtSeq: 0,
          DimList: {
            Dim: [],
          },
        };
      });

      // 4. Closed GL period check for Tax Date
      if (sumTax > 0 && taxInvDate && ClosePeriodGl) {
        const closeGlDate = new Date(ClosePeriodGl);
        if (taxInvDate <= closeGlDate) {
          rowErrors.push(
            `Row ${firstRowIdx}: Tax Invoice Date must be more than close period (ClosePeriodGl: ${format(closeGlDate, "dd/MM/yyyy")})`,
          );
        }
      }

      const rawInvoiceDesc =
        group.details[0]?.itemDescription || "Imported Invoice";
      const invhDesc =
        rawInvoiceDesc.length > 255
          ? rawInvoiceDesc.substring(0, 252) + "..."
          : rawInvoiceDesc;

      const invoiceObj = {
        InvhSeq: "Auto",
        VnCode: group.vendorNo || "",
        VnName: vendor ? vendor.VnVatName || vendor.VnName : "",
        InvhDate: invoiceDate || new Date(),
        InvhDesc: invhDesc,
        InvhSource: "XLS",
        InvhInvNo: group.invNo || "",
        InvhInvDate: invoiceDate || new Date(),
        InvhDueDate: invoiceDate
          ? addDays(invoiceDate, vendor ? parseInt(vendor.VnTerm || 0, 10) : 0)
          : new Date(),
        InvhCredit: vendor ? parseInt(vendor.VnTerm || 0, 10) : 0,
        InvhRef: "",
        CurCode: SettingSystem?.DefaultCurrencyCode || "THB",
        CurRate: SettingSystem?.DefaultCurrencyRate || 1,
        TaxProfileCode1: group.details[0]?.taxProfile || "NONE",
        InvhTaxT1: detailLines[0]?.InvdTaxT1 || "None",
        InvhTaxR1: NumberFormat(detailLines[0]?.InvdTaxR1 || 0),
        TaxProfileCode2: "NONE",
        InvhTaxT2: "None",
        InvhTaxR2: NumberFormat(0),
        InvhTotalAmt: NumberFormat(sumTotal),
        InvhTInvNo: sumTax > 0 ? group.taxInvNo || "" : "",
        InvhTInvDt: sumTax > 0 ? taxInvDate || new Date() : null,
        TaxPeriod:
          sumTax > 0
            ? taxInvDate
              ? format(taxInvDate, "MM/yyyy")
              : format(new Date(), "MM/yyyy")
            : "",
        TaxStatus:
          sumTax > 0 ? SettingAp?.DefaultTaxInvoiceStatus || "Pending" : "None",
        TaxId: vendor ? vendor.VnTaxNo : "",
        BranchNo: vendor ? vendor.BranchNo : "",
        InvWht: {
          WhtTypeCode: "",
          WhtTotalAmt: 0,
          WhtDeptCode: "",
          WhtRemark1: "",
          WhtRemark2: "",
          WhtNo: "",
          WhtTaxCr: "",
          Items: [],
        },
        Detail: detailLines,
        DimHList: {
          Dim: [],
        },
        UserModified: localStorage.getItem("UserName"),
      };

      return {
        invoice: invoiceObj,
        errors: rowErrors,
        importStatus: rowErrors.length === 0 ? "ready" : "in complete",
        sumNet,
        sumTax,
        sumTotal,
      };
    });

    setInvoices(list);
  }, [loadingLookups, jsonData, vendors, departments, accounts, taxProfiles]);

  const handleImport = async () => {
    const selectedInputDate = new Date(inputDate);
    if (
      !inputDate ||
      isNaN(selectedInputDate.getTime()) ||
      (SettingClosePeriod?.ClosePeriodAp &&
        selectedInputDate <= new Date(SettingClosePeriod.ClosePeriodAp))
    ) {
      SnackbarUtils.warning("Input Date must be more than close period");
      return;
    }

    const validInvoices = invoices.filter(
      (i) =>
        i.errors.length === 0 && ["ready", "failed"].includes(i.importStatus),
    );
    if (validInvoices.length === 0) {
      SnackbarUtils.warning("No valid invoices to import");
      return;
    }

    setImporting(true);
    let successCount = 0;
    let failCount = 0;
    const results = new Map();
    const failMessages = [];

    for (let i = 0; i < validInvoices.length; i++) {
      try {
        const item = validInvoices[i];
        const res = await createInvoiceDetail(
          { ...item.invoice, InvhDate: selectedInputDate },
          {
            suppressErrorSnackbar: true,
          },
        );
        if (res?.Code === 0) {
          successCount++;
          results.set(item, {
            importStatus: "created",
            resultMessage: res?.UserMessage,
          });
        } else {
          failCount++;
          const resultMessage =
            res?.UserMessage ||
            res?.InternalMessage ||
            "Failed to create invoice";
          results.set(item, {
            importStatus: res?.Code === -200 ? "duplicate" : "failed",
            resultMessage,
          });
          failMessages.push(resultMessage);
          console.error(
            "Failed to import invoice:",
            res?.InternalMessage || res?.UserMessage,
          );
        }
      } catch (err) {
        failCount++;
        const item = validInvoices[i];
        const resultMessage =
          err?.response?.data?.UserMessage ||
          err?.message ||
          "Failed to create invoice";
        results.set(item, { importStatus: "failed", resultMessage });
        failMessages.push(resultMessage);
        console.error("Error creating invoice:", err);
      }
    }

    setInvoices((current) =>
      current.map((item) =>
        results.has(item) ? { ...item, ...results.get(item) } : item,
      ),
    );
    setImporting(false);
    if (successCount > 0) {
      await SnackbarUtils.success(
        `Successfully imported ${successCount} invoice(s).`,
      );
      if (failCount === 0) onSuccess();
    }
    if (failCount > 0 && successCount === 0) {
      await SnackbarUtils.warning(failMessages.join("<br/>"));
    }
  };

  const hasAnyErrors = invoices.some((i) => i.errors.length > 0);
  const hasImportErrors = invoices.some(
    (i) => ["duplicate", "failed"].includes(i.importStatus) && i.resultMessage,
  );

  return (
    <Dialog
      open={open}
      onClose={importing ? undefined : onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle disableTypography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          style={{ gap: 16 }}
        >
          <Typography variant="h6">Import AP Invoice from Template</Typography>
          <DatePickerFormat
            label={translate("ra.field.Input Date")}
            value={inputDate}
            onChange={setInputDate}
            minDate={
              SettingClosePeriod?.ClosePeriodAp
                ? addDays(new Date(SettingClosePeriod.ClosePeriodAp), 1)
                : undefined
            }
            minDateMessage="Date must be more than close period"
            required
            disabled={importing}
            style={{ width: 180 }}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loadingLookups ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress size={40} style={{ marginRight: 16 }} />
            <Typography>Loading lookup records...</Typography>
          </Box>
        ) : invoices.length === 0 ? (
          <Typography color="textSecondary" align="center">
            No valid data lines found in template file
          </Typography>
        ) : (
          <Box>
            <Typography
              variant="subtitle1"
              gutterBottom
              style={{ fontWeight: 600 }}
            >
              Summary of Invoices to Create ({invoices.length})
            </Typography>
            <TableContainer
              component={Paper}
              style={{ marginBottom: 16, maxHeight: 300 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                    >
                      Vendor Code
                    </TableCell>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                    >
                      Invoice No
                    </TableCell>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                    >
                      Invoice Date
                    </TableCell>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                    >
                      Tax Invoice No
                    </TableCell>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                    >
                      Tax Invoice Date
                    </TableCell>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                      align="right"
                    >
                      Net Amount
                    </TableCell>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                      align="right"
                    >
                      Tax Amount
                    </TableCell>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                      align="right"
                    >
                      Total
                    </TableCell>
                    <TableCell
                      style={{ backgroundColor: "#34558b", color: "#fff" }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {item.invoice.VnCode || (
                          <span style={{ color: "red" }}>(Missing)</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.invoice.InvhInvNo || (
                          <span style={{ color: "red" }}>(Missing)</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.invoice.InvhInvDate instanceof Date &&
                        !isNaN(item.invoice.InvhInvDate.getTime()) ? (
                          format(item.invoice.InvhInvDate, "dd/MM/yyyy")
                        ) : (
                          <span style={{ color: "red" }}>
                            (Missing/Invalid)
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{item.invoice.InvhTInvNo || "-"}</TableCell>
                      <TableCell>
                        {item.invoice.InvhTInvDt instanceof Date &&
                        !isNaN(item.invoice.InvhTInvDt.getTime())
                          ? format(item.invoice.InvhTInvDt, "dd/MM/yyyy")
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {(item.sumNet || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="right">
                        {(item.sumTax || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="right">
                        {(item.sumTotal || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell>
                        <span
                          style={{
                            color:
                              item.importStatus === "created" ||
                              item.importStatus === "ready"
                                ? "green"
                                : item.importStatus === "duplicate"
                                  ? "#ed6c02"
                                  : "red",
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          {item.importStatus}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {hasAnyErrors && (
              <Box mt={2}>
                <Typography
                  variant="subtitle2"
                  color="error"
                  style={{ fontWeight: 600, marginBottom: 4 }}
                >
                  Please update the incomplete data in your Excel file as list
                  below and try again.
                </Typography>
                <Paper
                  style={{
                    maxHeight: 150,
                    overflow: "auto",
                    padding: 4,
                    backgroundColor: "#fff5f5",
                  }}
                >
                  <List dense style={{ padding: 0 }}>
                    {invoices.map((inv, idx) =>
                      inv.errors.map((err, errIdx) => (
                        <ListItem key={`${idx}-${errIdx}`} disableGutters>
                          <ListItemText
                            primary={
                              <Typography color="error" variant="body2">
                                • {err}
                              </Typography>
                            }
                          />
                        </ListItem>
                      )),
                    )}
                  </List>
                </Paper>
              </Box>
            )}

            {hasImportErrors && (
              <Box mt={2}>
                <Typography
                  variant="subtitle2"
                  color="error"
                  style={{ fontWeight: 600, marginBottom: 4 }}
                >
                  Import Errors:
                </Typography>
                <Paper
                  style={{
                    maxHeight: 150,
                    overflow: "auto",
                    padding: 4,
                    backgroundColor: "#fff5f5",
                  }}
                >
                  <List dense>
                    {invoices
                      .filter(
                        (inv) =>
                          ["duplicate", "failed"].includes(inv.importStatus) &&
                          inv.resultMessage,
                      )
                      .map((inv) => (
                        <ListItem
                          key={`${inv.invoice.VnCode}-${inv.invoice.InvhInvNo}`}
                          disableGutters
                        >
                          <ListItemText
                            primary={
                              <Typography color="error" variant="body2">
                                • duplicate invoice number | {inv.resultMessage}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                  </List>
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={importing}>
          {translate("ra.action.cancel")}
        </Button>
        <Button
          onClick={handleImport}
          color="primary"
          variant="contained"
          disabled={
            loadingLookups ||
            importing ||
            !invoices.some(
              (item) =>
                item.errors.length === 0 &&
                ["ready", "failed"].includes(item.importStatus),
            )
          }
        >
          {importing ? (
            <CircularProgress size={24} style={{ color: "#fff" }} />
          ) : (
            "Import"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogImport;
